/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback, useEffect, useMemo, useRef } from 'preact/hooks';

import { useFormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { useRichSelectionContext } from '#PRIVATE_RichSelectionCommon/RichSelectionContext';
import { StyledCard } from '#PRIVATE_StyledCard';
import { useCheckboxSetContext } from '#UNSAFE_CheckboxSet';
import { useRadioSetContext } from '#UNSAFE_RadioSet/RadioSetContext';
import { RichSelectionTextContent } from './RichSelectionTextContent';
import { RichSelectionMediaItem } from './RichSelectionMediaItem';
import { Flex } from '#UNSAFE_Flex';
import { styles } from './themes/RichSelectionItemStyles.css';
import { useId } from '#hooks/UNSAFE_useId';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { usePress } from '#hooks/UNSAFE_usePress';
import { RadioControl, CheckboxControl } from './RichSelectionControl';
import { type RichSelectionItemData } from './RichSelectionTypes';
import { useFocusableTextField } from '#hooks/UNSAFE_useFocusableTextField';
import { useRichSelectionStartContext } from '#PRIVATE_RichSelectionCommon/RichSelectionStartContext';
import { classNames } from '#utils/UNSAFE_classNames';
import { type TestIdProps } from '#hooks/UNSAFE_useTestId';
import { useRadioItemContext } from '#UNSAFE_RadioItem/RadioItemContext';

type RichSelectionItemProps<V extends string | number> = RichSelectionItemData<V> &
  TestIdProps & {
    /**
     * Specifies if the card is read-only.
     */
    isReadonly?: boolean;
  };

const onMouseDown = (event: Event) => {
  // Prevent focus from going to the body when the user clicks something within
  // the card that is not focusable. This is important because we are detecting
  // focus loss on the core pack side and showing hidden messages.
  event.preventDefault();
};

/**
 * `RichSelectionItem` is a customizable input card element used in selection interfaces. It can display a label,
 * optional secondary text, and media (such as an avatar, thumbnail, or icon). It supports both radio and checkbox
 * controls, allowing users to make a single or multiple selections, respectively. The component also handles
 * different states such as disabled, readonly, and focus, and renders dynamically based on layout( 'xl', 'md', or 'sm').
 */
export const RichSelectionItem = <V extends string | number>({
  avatar,
  iconClass,
  isReadonly: propsIsReadonly,
  mediaAccessibleLabel,
  secondaryText,
  thumbnailSrc,
  label,
  value,
  testId
}: RichSelectionItemProps<V>) => {
  const { isReadonly: isFormReadonly, isDisabled: isFormDisabled } = useFormContext();
  const styledCardRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputReadonlyRef = useRef<HTMLInputElement>(null);
  const { selection, layout, messageSeverity } = useRichSelectionContext()!;
  const { isDisabled: isFieldDisabled, isReadonly: isFieldReadonly } = useFormFieldContext();
  const { value: radioGroupValue, onCommit: onRadioCommit, setFocusIndex } = useRadioSetContext();
  const { value: checkboxGroupValue, onCommit: onCheckboxCommit } = useCheckboxSetContext();
  const { index } = useRadioItemContext();

  // Determining selection state for error
  const styledCardVariant = messageSeverity === 'error' ? 'danger' : undefined;
  const isDisabled = isFieldDisabled ?? isFormDisabled;
  const isReadonly = isFieldReadonly || isFormReadonly || propsIsReadonly;
  const isEnabled = !isDisabled && !isReadonly;
  const id = useId();
  const isLayoutXL = layout === 'xl';
  const hasMedia = !!avatar || !!thumbnailSrc || !!iconClass;
  const labelId = `${id}-labelled-text`;
  const secondaryTextId = secondaryText ? `${id}-secondary-text` : undefined;
  const isSingleSelection = selection === 'single';
  const isSelected = isSingleSelection
    ? radioGroupValue === value
    : !!checkboxGroupValue?.has(value);

  const {
    isFocused: isFocus,
    focusProps: { onFocusIn, onFocusOut },
    methods
  } = useFocusableTextField({
    enabledElementRef: inputRef,
    readonlyElementRef: inputReadonlyRef,
    isReadonly,
    isDisabled
  });

  // Scroll into view when the RichSelectionItem is focused
  useEffect(() => {
    if (isFocus) {
      styledCardRef?.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [isFocus, styledCardRef]);

  // Text content of label and secondaryText rendering
  const textContent = (
    <RichSelectionTextContent
      secondaryTextId={secondaryTextId}
      labelId={labelId}
      isDisabled={isDisabled}
      isLayoutXL={isLayoutXL}
      label={label}
      secondaryText={secondaryText}
    />
  );

  // Media content rendering based on available media props respective to layout
  const media = avatar
    ? { avatar, mediaAccessibleLabel, layout: layout as 'md' | 'sm' }
    : thumbnailSrc
    ? { thumbnailSrc, mediaAccessibleLabel, layout: layout as 'xl' | 'md' | 'sm' }
    : iconClass
    ? { iconClass, layout: layout as 'sm' }
    : undefined;

  const mediaContent = hasMedia ? (
    <RichSelectionMediaItem
      {...media}
      isDisabled={isDisabled}
      hasSecondaryText={!!secondaryText}
      testId={testId ? `${testId}_media` : undefined}
    />
  ) : undefined;

  // Selection control rendering (radio or checkbox)
  const selectionControl = (
    <Flex align={secondaryText ? 'start' : 'center'}>
      {isSingleSelection ? (
        <RadioControl
          ariaLabel={label}
          onFocus={onFocusIn}
          onBlur={onFocusOut}
          value={value}
          inputRef={inputRef}
          isFocus={isFocus}
          isLayoutXL={isLayoutXL}
          isDisabled={isDisabled}
          isReadonly={isReadonly}
          isSelected={isSelected}
          describedBy={secondaryTextId}
          labelledBy={`${id}-labelled-text`}
        />
      ) : (
        <CheckboxControl
          ariaLabel={label}
          onFocus={onFocusIn}
          onBlur={onFocusOut}
          value={value}
          inputRef={inputRef}
          inputReadonlyRef={inputReadonlyRef}
          isFocus={isFocus}
          isDisabled={isDisabled}
          isLayoutXL={isLayoutXL}
          isReadonly={isReadonly}
          isSelected={isSelected}
          describedBy={secondaryTextId}
          labelledBy={`${id}-labelled-text`}
        />
      )}
    </Flex>
  );

  // Handler for checkbox selection changes
  const triggerCheckBoxAction = useCallback(() => {
    // if selected from a click, we need to manually set focus on the input to allow for keyboard navigation
    methods.focus();

    const newGroupValue = new Set(checkboxGroupValue);
    // If the value is already selected, remove it; otherwise, add it
    if (newGroupValue.has(value)) {
      newGroupValue.delete(value);
    } else {
      newGroupValue.add(value);
    }
    onCheckboxCommit?.({
      previousValue: checkboxGroupValue,
      value: newGroupValue.size > 0 ? newGroupValue : undefined
    });
  }, [checkboxGroupValue, methods, onCheckboxCommit, value]);

  // Handler for radio selection changes
  const triggerRadioAction = useCallback(() => {
    if (radioGroupValue !== value) {
      // if selected from a click, we need to manually set focus on the input to allow for keyboard navigation
      methods.focus();

      onRadioCommit?.({
        previousValue: radioGroupValue,
        value: value
      });
    }
  }, [radioGroupValue, value, methods, onRadioCommit]);

  // due to the placement of the start label in rich selection, we need to check if the first item contains media or secondary text.
  const startData = useMemo(
    () => ({ hasMedia, hasSecondaryText: !!secondaryText }),
    [hasMedia, secondaryText]
  );
  const richSelectionStartContext = useRichSelectionStartContext();
  if (richSelectionStartContext?.isFirst) {
    richSelectionStartContext?.setStartData?.(startData);
  }

  // Event handlers for the interaction of the StyledCard
  const { pressProps } = usePress(isSingleSelection ? triggerRadioAction : triggerCheckBoxAction, {
    isDisabled: !isEnabled
  });

  // This is used to apply focus to the card while in readonly mode
  const onReadonlyClickHandler = useCallback(() => {
    // focus if radio
    inputRef?.current?.focus();
    setFocusIndex?.(index);

    // focus if checkbox
    inputReadonlyRef?.current?.focus();
  }, [index, setFocusIndex]);

  return (
    <StyledCard
      cursor={isEnabled ? 'pointer' : 'arrow'}
      display="inlineFlex"
      height="100%"
      isDisabled={!isEnabled}
      isFocusWithin={isFocus}
      isReadonly={isReadonly}
      isSelected={styledCardVariant !== 'danger' && isEnabled && isSelected}
      onMouseDown={onMouseDown}
      parentComponentVariant="richSelectionItemCard"
      ref={styledCardRef}
      role="listitem"
      styleVariant={styledCardVariant}
      testId={testId ? `${testId}_styled_card` : undefined}
      width="100%"
      {...(isReadonly ? { onClick: onReadonlyClickHandler } : pressProps)}>
      {isLayoutXL ? (
        <Flex direction="column" height="100%" width="100%">
          {mediaContent}
          <div class={styles.selectionElement}>{selectionControl}</div>
          {textContent}
        </Flex>
      ) : (
        <div
          className={classNames([
            styles.densityWrapper,
            // if the item has a sm/md layout, without media or secondary text,
            // we need to set the min height of the card to match the start label
            !isLayoutXL &&
              !hasMedia &&
              !secondaryText &&
              richSelectionStartContext !== null &&
              styles.startMinHeight
          ])}>
          <Flex direction="row" gap="4x" height="100%" width="100%">
            {selectionControl}
            {textContent}
            {mediaContent}
          </Flex>
        </div>
      )}
    </StyledCard>
  );
};
